import { ArrowNarrowLeft, SearchLg, SearchMd, XClose } from '@untitledui/icons'
import { type KeyboardEvent, useEffect, useRef } from 'react'
import { twJoin } from 'tailwind-merge'

import { Input, type InputIcon } from '@components/designSystem/input/input'

interface SearchInputProps {
  placeholder?: string
  value?: string
  onChange: (value: string) => void
  onSearch: () => void
  onCancel: () => void
  onBack: () => void
  onActivate: () => void

  onFocus?: () => void
  onBlur?: () => void

  isInert?: boolean
  autoFocus?: boolean
}

function SearchInput({ placeholder, value, onSearch, onChange, onCancel, onBack, onActivate, onFocus, onBlur, isInert = false, autoFocus = false }: SearchInputProps) {
  const ref = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isInert || !autoFocus || !ref.current) return

    ref.current.focus()
    ref.current.select()
  }, [isInert, autoFocus])

  if (isInert) {
    return (
      <div onClick={onActivate}>
        <Input
          type='text'
          data-testid='search-input'
          size='lg'
          leadingIcon={{ icon: SearchLg }}
          placeholder={placeholder}
          isReadOnly={true}
        />
      </div>
    )
  }

  const leadingIcon = { icon: ArrowNarrowLeft, onClick: onBack, label: 'back', className: 'text-primary' }

  const iconClass = 'transition-all duration-200 starting:scale-0 starting:opacity-0'

  const trailingIcons: [InputIcon, InputIcon] | undefined = value
    ? [
      { icon: XClose, onClick: onCancel, label: 'cancel', className: iconClass },
      { icon: SearchMd, onClick: onSearch, label: 'search', className: twJoin('stroke-[2.5px] text-primary', iconClass) },
    ]
    : undefined

  function changeHandler(searchTerm: string) {
    if (searchTerm === '') return onCancel()
    onChange(searchTerm)
  }

  function handleEnter(ev: KeyboardEvent) {
    if (ev.key !== 'Enter') return
    ref.current?.blur()
    onSearch()
  }

  return (
    <Input
      ref={ref}
      type='text'
      size='lg'
      data-testid='search-input'
      leadingIcon={leadingIcon}
      trailingIcons={trailingIcons}
      inputClassName='transition-[padding] duration-200'
      placeholder={placeholder}
      onChange={changeHandler}
      onKeyDown={handleEnter}
      value={value}
      onFocus={onFocus}
      onBlur={onBlur}
    />
  )
}

export default SearchInput